import { Check } from 'lucide-react';
import { Fragment } from 'react';
import type { LifecycleStage, QuoteStat } from './types';
import { MicroLabel } from './ui/primitives';

/**
 * The promoted overview: the four numbers that decide whether this quote needs
 * attention, over the stage it has reached.
 *
 * Promoted out of the metadata rail on purpose. These are the first questions
 * asked of a quote — what is it worth, what does it earn, when must the client
 * answer — and they were previously three columns of a strip that said only how
 * many products and offers there were.
 */
export function QuoteOverview({ stats, stages }: { stats: QuoteStat[]; stages: LifecycleStage[] }) {
	return (
		/* Card white, the way the order page's overview is: this is the first box
		   on the page, and a sunken one reads as a footer under the header rather
		   than the top of the document. The lifecycle strip below keeps the sunken
		   tone, which is what separates the two halves now that the cells don't. */
		<div className="mb-2 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface">
			<div
				className="grid border-b border-line-1"
				style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}
			>
				{stats.map((stat) => (
					<div
						key={stat.label}
						className="px-5 py-4"
						/* Hairlines drawn as insets rather than borders: the cells wrap at
						   narrow widths, and a border would leave a stray edge on whichever
						   cell happens to end a row. */
						style={{
							boxShadow: '-0.5px 0 0 var(--line-1), 0 -0.5px 0 var(--line-1)',
						}}
					>
						<MicroLabel className="mb-1.5 block">{stat.label}</MicroLabel>
						<div className="font-mono text-2xl font-medium whitespace-nowrap text-text-1">
							{stat.value}
						</div>
						<div className="mt-1 text-xs text-text-4">{stat.hint ?? ' '}</div>
					</div>
				))}
			</div>
			<QuoteLifecycle stages={stages} />
		</div>
	);
}

/**
 * Where the quote is, as a line rather than a list — the connector between two
 * stages is the point, since a quote only ever moves forward through them.
 */
export function QuoteLifecycle({ stages }: { stages: LifecycleStage[] }) {
	return (
		<div className="flex items-center gap-2 bg-surface-sunken px-5 py-3">
			{stages.map((stage, i) => {
				const done = stage.state === 'done';
				const current = stage.state === 'current';
				return (
					<Fragment key={stage.key}>
						<span className="inline-flex shrink-0 items-center gap-2">
							<span
								className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
								style={{
									background: done ? 'var(--accent)' : 'transparent',
									boxShadow: done
										? 'none'
										: `inset 0 0 0 1px ${current ? 'var(--accent)' : 'var(--line-3)'}`,
								}}
							>
								{done && (
									<Check size={9} strokeWidth={3} className="text-accent-fg" />
								)}
								{current && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
							</span>
							<span
								className="text-sm whitespace-nowrap"
								style={{
									fontWeight: current ? 500 : 400,
									color:
										stage.state === 'upcoming'
											? 'var(--text-4)'
											: 'var(--text-1)',
								}}
							>
								{stage.label}
							</span>
							{stage.date && (
								<span className="font-mono text-2xs text-text-4">{stage.date}</span>
							)}
						</span>
						{i < stages.length - 1 && (
							<span
								className="h-px flex-1"
								style={{
									background: done ? 'var(--accent)' : 'var(--line-2)',
									opacity: done ? 0.5 : 1,
								}}
							/>
						)}
					</Fragment>
				);
			})}
		</div>
	);
}
